import { queryOptions, type QueryClient } from '@tanstack/react-query';

import type { MatchingProjectDto } from '@/api/core-api/types.gen';

import { featureFlagsControllerGetFeatureFlagActive } from '@/api/core-api/sdk.gen';
import { DashboardExperimentArm } from '@/api/core-api/types.gen';
import { queryKeys } from '@/hooks/api/query-keys';
import { getClient } from '@/utils/get-api-client';

/** core-api feature flag that enrolls anyone at all. Off = kill switch. */
export const DASHBOARD_MATCHED_SURFACING_FLAG = 'dashboard-matched-surfacing';

/**
 * Hash salt. Changing it reshuffles every participant's arm, so it stays
 * fixed for the life of the experiment; a follow-up experiment gets a new one.
 */
export const DASHBOARD_EXPERIMENT_SALT = 'dashboard-matched-surfacing-v0';

/** Share of participants enrolled at all. */
export const EXPERIMENT_TRAFFIC_SHARE = 1;
/** Share of the enrolled who get the treatment. */
export const EXPERIMENT_TREATMENT_SHARE = 1;

export type CardSurface = 'feed' | 'topPicks';
export type AnalyticsArm = 'control' | 'treatment' | 'none';

/**
 * Deterministic bucket in [0, 1) from SHA-256(salt:userId), first 4 bytes.
 * The warehouse SQL equivalent lives in dashboard-matched-surfacing.md.
 */
export async function bucketForUser(
  userId: string,
  salt: string = DASHBOARD_EXPERIMENT_SALT,
): Promise<number> {
  const bytes = new TextEncoder().encode(`${salt}:${userId}`);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return new DataView(digest).getUint32(0) / 2 ** 32;
}

export async function assignDashboardExperimentArm(
  userId: string,
): Promise<DashboardExperimentArm | null> {
  const bucket = await bucketForUser(userId);
  if (bucket >= EXPERIMENT_TRAFFIC_SHARE) return null;
  return bucket < EXPERIMENT_TRAFFIC_SHARE * EXPERIMENT_TREATMENT_SHARE
    ? DashboardExperimentArm.TREATMENT
    : DashboardExperimentArm.CONTROL;
}

export const dashboardExperimentFlagQueryOptions = (authUserId: string) =>
  queryOptions({
    queryKey: queryKeys.featureFlags.dashboardMatchedSurfacing,
    queryFn: () =>
      featureFlagsControllerGetFeatureFlagActive({
        client: getClient(authUserId),
        path: { name: DASHBOARD_MATCHED_SURFACING_FLAG },
        throwOnError: true,
      }).then((res) => res.data === true),
    staleTime: 60_000,
  });

/**
 * Runs in the dashboard route loader (server on SSR, browser on SPA
 * navigation). Any failure resolves to null: today's dashboard, nobody
 * enrolled, nothing thrown.
 *
 * `fetchQuery` rather than `ensureQueryData`: nothing observes this query
 * outside the loader, so a cached flag would otherwise be reused until it is
 * garbage-collected and the kill switch would miss in-app navigation.
 */
export async function resolveDashboardExperimentArm(
  queryClient: QueryClient,
  userId: string,
): Promise<DashboardExperimentArm | null> {
  try {
    const active = await queryClient.fetchQuery(
      dashboardExperimentFlagQueryOptions(userId),
    );
    if (!active) return null;
    return await assignDashboardExperimentArm(userId);
  } catch (error) {
    console.error(
      '[experiments] dashboard-matched-surfacing unavailable',
      error,
    );
    return null;
  }
}

/** Matched means a positive v4 score on this project. */
export function isMatched(
  project: Pick<MatchingProjectDto, 'v4Score'>,
): boolean {
  return (project.v4Score ?? 0) > 0;
}

export function isBoosted(
  project: Pick<MatchingProjectDto, 'boostProjectFeed'>,
): boolean {
  return project.boostProjectFeed === true;
}

export function armForAnalytics(
  arm: DashboardExperimentArm | null,
): AnalyticsArm {
  return arm ?? 'none';
}
